import { useNavigate } from 'react-router-dom';
import { Clock, ChevronDown } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  useMyClosingRodeos,
  dashboardDataKeys,
  type ClosingRodeo,
} from '@/api/extensions/useDashboardData';
import { CountdownTimer } from '../shared_reusable_components/CountdownTimer';

/**
 * Global "entries closing soon" indicator — split across two surfaces so the
 * header doesn't get squished on mobile:
 *
 * - **md and up (≥768px)**: rendered inline inside the AppHeader's right-hand
 *   action row by {@link EntryCloseHeaderIndicator}. Shows up to two amber
 *   pills inline plus a "+N more" popover for the rest. Plenty of room next
 *   to the bell / balance / theme / user menu.
 * - **<md (mobile)**: the inline header pills are hidden, and a dedicated
 *   full-width amber bar — {@link EntryCloseMobileBar} — sits directly below
 *   the header (sticky `top-14` so it tracks the header when the page
 *   scrolls). At narrow widths there isn't horizontal room for a usable
 *   countdown pill *and* the other header actions in a single 56px row;
 *   stacking the bar underneath preserves the persistent visibility that
 *   motivated putting the timer in the header originally without forcing
 *   the header to grow taller than its design height.
 *
 * Both surfaces read from the same `useMyClosingRodeos` query (React Query
 * dedupes the request) so the desktop pill list and the mobile bar can
 * never display different data.
 */

/* --------------------------------------------------------------------- */
/* Desktop (≥md) — inline pills in the AppHeader                          */
/* --------------------------------------------------------------------- */

export function EntryCloseHeaderIndicator() {
  const { data: rodeos } = useMyClosingRodeos();
  const [popoverOpen, setPopoverOpen] = useState(false);

  if (!rodeos || rodeos.length === 0) return null;

  const overflow = rodeos.slice(2); // beyond the 2 inline desktop pills

  // The whole indicator is desktop-only. The wrapper `hidden md:flex`
  // means at <md it occupies no space at all and the EntryCloseMobileBar
  // (mounted in AppLayout) is what surfaces the same data.
  return (
    <div
      className="hidden md:flex items-center gap-2"
      data-testid="entry-close-header-indicator"
    >
      <span
        aria-hidden
        className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider text-amber-200/90"
      >
        Entries Closing
      </span>

      <CountdownPill rodeo={rodeos[0]} variant="header" />
      {rodeos[1] && <CountdownPill rodeo={rodeos[1]} variant="header" />}

      {overflow.length > 0 && (
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Show more rodeos closing soon"
              className="inline-flex items-center gap-1 rounded-md border border-amber-400/40 bg-amber-500/15 px-2 py-1 text-xs font-semibold text-amber-100 hover:bg-amber-500/25 hover:border-amber-400/60 transition-colors"
            >
              <span>+{overflow.length}</span>
              <ChevronDown className="h-3 w-3 opacity-70" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <OverflowList
              rodeos={overflow}
              onItemClick={() => setPopoverOpen(false)}
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* Mobile (<md) — full-width bar sticky below the header                  */
/* --------------------------------------------------------------------- */

/**
 * Mobile-only stacked bar that lives directly beneath the AppHeader. Mount
 * once at the top of the main layout body. Self-hides when no rodeos are
 * within the closing window so it adds no chrome in the common case.
 *
 * `sticky top-14` keeps it pinned just below the 56px sticky header as the
 * page scrolls; `z-30` sits below the header's `z-40` so the header's
 * border still draws cleanly over the top of the bar.
 */
export function EntryCloseMobileBar() {
  const { data: rodeos } = useMyClosingRodeos();
  const [popoverOpen, setPopoverOpen] = useState(false);

  if (!rodeos || rodeos.length === 0) return null;

  const overflow = rodeos.slice(1); // beyond the 1 inline mobile pill

  return (
    <div
      className="md:hidden sticky top-14 z-30 border-b border-amber-400/30 bg-amber-500/10 backdrop-blur supports-[backdrop-filter]:bg-amber-500/15"
      role="region"
      aria-label="Entries closing soon"
      data-testid="entry-close-mobile-bar"
    >
      <div className="flex items-center gap-2 px-3 py-1.5">
        <span
          aria-hidden
          className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-200/90 shrink-0"
        >
          Closing
        </span>

        <CountdownPill
          rodeo={rodeos[0]}
          variant="mobile-bar"
          className="flex-1 min-w-0"
        />

        {overflow.length > 0 && (
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Show more rodeos closing soon"
                className="inline-flex items-center gap-1 rounded-md border border-amber-400/40 bg-amber-500/15 px-2 py-1 text-xs font-semibold text-amber-900 dark:text-amber-100 hover:bg-amber-500/25 hover:border-amber-400/60 transition-colors shrink-0"
              >
                <span>+{overflow.length}</span>
                <ChevronDown className="h-3 w-3 opacity-70" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
              <OverflowList
                rodeos={overflow}
                onItemClick={() => setPopoverOpen(false)}
              />
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* Shared internals                                                       */
/* --------------------------------------------------------------------- */

interface CountdownPillProps {
  rodeo: ClosingRodeo;
  /**
   * `header` — used on the dark AppHeader (amber-100 text on dark bg).
   * `mobile-bar` — used in the lighter sticky bar; adapts text colour
   * for light/dark page background.
   */
  variant: 'header' | 'mobile-bar';
  className?: string;
}

function CountdownPill({ rodeo, variant, className }: CountdownPillProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const name = rodeo.name || 'Unnamed rodeo';

  const handleExpire = () => {
    queryClient.invalidateQueries({
      queryKey: dashboardDataKeys.myClosingRodeos(),
    });
  };

  const baseClasses =
    'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors';

  const variantClasses =
    variant === 'header'
      ? 'border-amber-400/40 bg-amber-500/15 text-amber-100 hover:bg-amber-500/25 hover:border-amber-400/60'
      : 'border-amber-400/40 bg-amber-500/15 text-amber-900 dark:text-amber-100 hover:bg-amber-500/25 hover:border-amber-400/60';

  // The mobile bar gives us the full viewport width — show the rodeo name
  // alongside the countdown so the user sees *which* rodeo is closing,
  // not just "something is". Truncates if the name is long.
  const nameMaxWidth =
    variant === 'header' ? 'max-w-[140px]' : 'max-w-full flex-1';

  return (
    <button
      type="button"
      onClick={() => navigate(`/rodeos/${rodeo.id}`)}
      title={`${name} — entries closing soon, click to enter`}
      aria-label={`${name} entries closing soon, click to enter`}
      className={`${baseClasses} ${variantClasses} ${className ?? ''}`}
    >
      <Clock className="h-3.5 w-3.5 shrink-0" />
      <span className={`truncate ${nameMaxWidth}`}>{name}</span>
      <span className="opacity-70 shrink-0">·</span>
      <CountdownTimer
        target={rodeo.entry_close_time}
        variant="compact"
        onExpire={handleExpire}
        className="font-mono tabular-nums shrink-0"
      />
    </button>
  );
}

interface OverflowListProps {
  rodeos: ClosingRodeo[];
  onItemClick: () => void;
}

function OverflowList({ rodeos, onItemClick }: OverflowListProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleExpire = () => {
    queryClient.invalidateQueries({
      queryKey: dashboardDataKeys.myClosingRodeos(),
    });
  };

  return (
    <div>
      <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground border-b">
        More closing soon
      </div>
      <ul className="divide-y max-h-80 overflow-y-auto">
        {rodeos.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => {
                onItemClick();
                navigate(`/rodeos/${r.id}`);
              }}
              className="w-full text-left px-3 py-2.5 hover:bg-muted/60 transition-colors"
            >
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-semibold text-sm truncate">
                  {r.name || 'Unnamed rodeo'}
                </span>
                {r.rodeo_or_jackpot === 'jackpot' && (
                  <span className="text-[10px] uppercase rounded border px-1 py-0.5 text-muted-foreground">
                    Jackpot
                  </span>
                )}
              </div>
              <CountdownTimer
                target={r.entry_close_time}
                variant="compact"
                onExpire={handleExpire}
                className="font-mono tabular-nums text-xs text-amber-700 dark:text-amber-300"
              />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
